import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Smile,
  Meh,
  Frown,
  Heart,
  Clock,
  Compass,
  MessageCircle,
  Check,
} from "lucide-react";
import { toast } from "sonner";

const SUBJECTS = [
  { key: "feel", labelKey: "qol.subject.feel", icon: Heart },
  { key: "past", labelKey: "qol.subject.past", icon: Clock },
  { key: "future", labelKey: "qol.subject.future", icon: Compass },
  { key: "inner_self_talk", labelKey: "qol.subject.inner_self_talk", icon: MessageCircle },
];

const OPTIONS = [
  { value: "good", labelKey: "qol.option.good", icon: Smile, color: "#4A5D4E" },
  { value: "neutral", labelKey: "qol.option.neutral", icon: Meh, color: "#B37A5F" },
  { value: "bad", labelKey: "qol.option.bad", icon: Frown, color: "#9C6A53" },
];

/**
 * Login-time QoL check-in modal.
 *
 * Shown on the first visit to the QoL page each calendar day. Contains the
 * same four toggles as the main QoL page so the user can update anything
 * that has changed since their last visit, or click "Klopt allemaal nog" to
 * confirm and dismiss.
 *
 * State (feel/past/future/inner_self_talk + updated_at) is passed in from
 * the parent (already loaded). Any change fires PUT /qol/me for the affected
 * subject, mirroring the QoL page behavior.
 */
export default function QolCheckInModal({ initialState, onStateChange, onClose }) {
  const { t } = useT();
  const [state, setState] = useState(initialState);
  const [pending, setPending] = useState({});
  const dialogRef = useRef(null);

  // Lock body scroll while modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Focus the dialog for a11y.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const setValue = async (key, value) => {
    if (!value) return; // toggle-group emits empty when deselecting
    const prev = state[key];
    setState((s) => ({ ...s, [key]: value }));
    setPending((p) => ({ ...p, [key]: true }));
    try {
      const { data } = await api.put("/qol/me", { [key]: value });
      onStateChange?.({ ...state, [key]: value, updated_at: data.updated_at });
    } catch (e) {
      setState((s) => ({ ...s, [key]: prev }));
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPending((p) => ({ ...p, [key]: false }));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 sm:py-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="qol-checkin-title"
      data-testid="qol-checkin-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[#1A1D1A]/40 backdrop-blur-[2px]"
        data-testid="qol-checkin-backdrop"
      />

      {/* Panel */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="relative w-full max-w-2xl bg-[#F9F8F6] border border-[#E5E2DC] rounded-2xl shadow-2xl p-6 sm:p-10 max-h-[90vh] overflow-y-auto focus:outline-none"
      >
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188]">
          {t("brand.tagline")}
        </p>
        <h2
          id="qol-checkin-title"
          className="mt-2 font-heading text-3xl sm:text-4xl text-[#1A1D1A] tracking-tight font-light leading-tight"
        >
          {t("checkin.title")}
        </h2>
        <p className="mt-3 text-sm sm:text-base text-[#5C635A] leading-relaxed">
          {t("checkin.subtitle")}
        </p>

        <div className="mt-8 space-y-4" data-testid="qol-checkin-subjects">
          {SUBJECTS.map(({ key, labelKey, icon: Icon }) => {
            const current = state?.[key] ?? null;
            return (
              <div
                key={key}
                className="bg-white border border-[#E5E2DC] rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                data-testid={`qol-checkin-subject-${key}`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-[#F2F0EA] flex items-center justify-center shrink-0">
                    <Icon strokeWidth={1.5} className="text-[#B37A5F]" size={18} />
                  </div>
                  <h3 className="font-heading text-lg text-[#1A1D1A] tracking-tight font-semibold">
                    {t(labelKey)}
                  </h3>
                </div>
                <ToggleGroup
                  type="single"
                  value={current || ""}
                  onValueChange={(v) => setValue(key, v)}
                  className="gap-1.5 self-start sm:self-center"
                  data-testid={`qol-checkin-toggle-${key}`}
                  disabled={pending[key]}
                >
                  {OPTIONS.map(({ value, labelKey: optLabelKey, icon: OptIcon, color }) => {
                    const active = current === value;
                    const optLabel = t(optLabelKey);
                    return (
                      <ToggleGroupItem
                        key={value}
                        value={value}
                        aria-label={optLabel}
                        data-testid={`qol-checkin-${key}-${value}`}
                        className={`h-10 px-3 rounded-lg border text-sm transition-colors ${
                          active
                            ? "text-white"
                            : "bg-white border-[#E5E2DC] text-[#5C635A] hover:bg-[#F2F0EA] hover:text-[#1A1D1A]"
                        }`}
                        style={
                          active
                            ? { backgroundColor: color, borderColor: color, color: "#fff" }
                            : undefined
                        }
                      >
                        <OptIcon strokeWidth={1.5} size={14} className="mr-1" />
                        {optLabel}
                      </ToggleGroupItem>
                    );
                  })}
                </ToggleGroup>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold max-w-xs">
            {t("checkin.close_hint")}
          </p>
          <Button
            type="button"
            onClick={onClose}
            data-testid="qol-checkin-confirm-button"
            className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white px-6"
          >
            <Check strokeWidth={1.5} size={18} className="mr-2" />
            {t("checkin.confirm_button")}
          </Button>
        </div>
      </div>
    </div>
  );
}
